/**
 * Zur Abrechnung freigeben: Marvins Seite.
 *
 * ORIGINALTEXT MARVIN (23.09.2026)
 *   „wenn das LEB freigegeben wurde von der gutschrift bekommen die firmen
 *    ihre info und ich lasse sie erstmal ihre rechnung schreiben."
 *   „1. erstmal durch meine prüfung, später automatisch"
 *
 * JE FIRMA EIN BLOCK, je Auftrag und Gewerk eine Zeile. Vorangehakt ist,
 * wo die Gutschrift auf den Wert des LEB passt; eine Zeile mit „zu wenig"
 * oder „unklar" steht da, ist aber nicht angehakt - dort soll Marvin
 * hinsehen, bevor er sie freigibt.
 *
 * Die Firma erfährt davon nur, DASS sie abrechnen kann. Keine Position, keine
 * Menge (siehe `api/src/abrechnungsfreigabe.ts`).
 *
 * JE FIRMA EIN ARBEITSPLATZ (.358). ORIGINALTEXT MARVIN (14.10.2026)
 *   „Ich würde es begrüßen, wenn wir ... mit den Gutschriften, dass ich das
 *    pro Firma oben selektieren kann, anstatt in einer großen Liste zu haben.
 *    Wenn die Gutschrift über unseren Wert ist, dann kann das nicht in Grau
 *    angezeigt werden, das geht unter. Es muss in Rot angezeigt werden und
 *    dann muss das auch anklickbar sein. ... Hinweise ... über einen
 *    Mausover mit einem Fragezeichen."
 *   „von den Entwürfen kannst du alles einbauen, außer den Ablauf"
 *
 * Oben die Firmen als Reiter (Zahl der Aufträge, rot: wie viele über unserem
 * Wert). Je Auftrag eine Zeile mit drei Werten nebeneinander: unser Wert,
 * Gutschrift, Abgleich. Rot ist ein Knopf und klappt die Positionen auf
 * (Aufmaß der Firma gegen Gutschrift, nur Mengen). Hinweise stehen als „?"
 * in der Zeile. Was passt, ist vorgewählt und geht mit EINEM Knopf als
 * Nachricht an die Firma.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMerkDaten, useMerkState, useRollstelle } from './merkplatz.ts';
import { api, darf, datum, euro } from '../api.ts';
import { extern } from './extern.ts';
import { Karte } from './Geruest.tsx';
import { Fehlerkasten, ausFehler } from './Formular.tsx';
import { useFrisch } from './Frisch.tsx';
import { NummernKopieren, Prueffenster, RechnungChip } from './Rechnungspruefung.tsx';
import type { Anmelder } from '../api.ts';
import { anschriftVon, passtSuche } from './Schluesselsuche.tsx';
import { Gutschriftliste } from './Gutschriftliste.tsx';
import { fragen, eingeben } from './rueckfrage.tsx';

const GEWERK: Record<string, string> = { tiefbau: 'Tiefbau', montage: 'Montage' };
/** Strasse, Hausnummer mit Zusatz, PLZ und Ort in einer Zeile. */
export const voll = (z: any) => {
  const haus = `${z.strasse ?? ''} ${z.hausnummer ?? ''}${z.hausnummer_zusatz ?? ''}`.trim();
  const ort = `${z.plz ?? ''} ${z.ort_text ?? ''}`.trim();
  return [haus, ort].filter(Boolean).join(', ');
};
const schluessel = (z: any) => `${z.auftrag_id}|${z.subunternehmer_id}|${z.gewerk_gruppe}|${z.gutschrift_id ?? ''}`;

/* DIE PUSH-NACHRICHT, SO WIE SIE RAUSGEHT (.340): dieselben Zeilen wie am
   Server (`abrechnungsfreigabe.ts`). Mengen, nie Preise. */
/** DER SATZ VOR DEN ABGEWÄHLTEN POSITIONEN (.410), wörtlich wie am Server. */
export const NICHT_SATZ = '   Nicht auf die Rechnung (nicht deine Position):';
/** Die Kennung einer Positionszeile, die sich abwählen lässt. */
export const posKennung = (p: any, i: number) => String(p.zeile ?? `${p.oz}#${i}`);
/** Der Wortlaut einer abgewählten Position für die Nachricht: OZ, Text, Vermerk, Menge; nie Preise. */
export const posWort = (p: any) =>
  `${p.oz}${p.bezeichnung ? ' ' + p.bezeichnung : ''}${p.vermerk ? ` (${p.vermerk})` : ''}: ${mengeText(p.menge_gutschrift ?? p.menge_leb ?? 0, p.einheit)}`;
/** Die abgewählten Positionen einer Zeile (.410), als Wortlaut für Vorschau und Server. */
export function nichtZeilen(z: any, k: string, nicht: Set<string>): string[] {
  return (z.vergleich ?? []).map((p: any, i: number) => ({ p, i }))
    .filter(({ p, i }: any) => !p.fremd && nicht.has(`${k}|${posKennung(p, i)}`))
    .map(({ p }: any) => posWort(p));
}
/** .612: die nicht angehakten Positionen als „OZ|Vermerk": ihre Änderung steht NICHT im Push. */
export function nichtPos(z: any, k: string, nicht: Set<string>): string[] {
  return (z.vergleich ?? []).map((p: any, i: number) => ({ p, i }))
    .filter(({ p, i }: any) => !p.fremd && nicht.has(`${k}|${posKennung(p, i)}`))
    .map(({ p }: any) => `${p.oz}|${p.vermerk ?? ''}`);
}
/** Gehört eine Textzeile („OZ Text (Vermerk): …") zu dieser Position? */
export function zeileGehoertZu(s: string, schluessel: string): boolean {
  const [oz, vermerk] = schluessel.split('|');
  return String(s).trim().startsWith(`${oz} `) || String(s).trim().startsWith(`${oz}:`)
    ? (!vermerk || String(s).includes(`(${vermerk})`)) : false;
}
/** Der kurze Standardtext, wenn nichts zu ändern ist; wörtlich wie am Server (api/abrechnungsfreigabe.ts). */
export const standardSatz = (g: string) => g === 'montage' ? '   Bitte Rechnung für Montage einreichen.' : '   Bitte Rechnung für Tiefbau wie in deinem Aufmaß einreichen.';
export function pushVorschau(zeilen: any[]): string {
  const teile: string[] = [`✅ Zur Abrechnung freigegeben (${zeilen.length})`, ''];
  for (const z of zeilen) {
    const kenn = [z.kls_id ? `KLS ${z.kls_id}` : null, z.sm_nummer ? `SM ${z.sm_nummer}` : null].filter(Boolean);
    /* OHNE GUTSCHRIFTNUMMER (.399): die geht die Partner nichts an, siehe api/abrechnungsfreigabe.ts. */
    teile.push(`📍 ${voll(z) || 'ohne Anschrift'}${kenn.length ? ` (${kenn.join(', ')})` : ''}, ${GEWERK[z.gewerk_gruppe] ?? z.gewerk_gruppe}`);
    /* DER FREITEXT (.399), wie im Server unter der Zeile. */
    if (String(z.hinweis ?? '').trim()) teile.push(`   Hinweis: ${String(z.hinweis).trim()}`);
    /* NICHT DEINE POSITION (.410), dieselben Zeilen wie am Server. */
    const nicht: string[] = z.nicht_zeilen ?? [];
    if (nicht.length) {
      teile.push(NICHT_SATZ);
      for (const n of nicht) teile.push(`   ${n}`);
    }
    const ab: string[] = z.abweichung_zeilen ?? [];
    if (ab.length) {
      /* DIE RECHNUNG ÄNDERT SICH, NICHT DAS AUFMASS (.375). Marvin, 16.10.2026:
         „das heißt er muss seine rechnung anpassen, nicht das aufmaß im
         system." Das Aufmaß ist eingereicht und von der Telekom beschieden;
         die Firma schreibt die Rechnung nach dem, was gutgeschrieben wurde. */
      /* DER SATZ (.389). Marvin, 17.10.2026: „Rechnung oder Positionen müssen
         wie folgt geändert werden. Und dann die Positionen aufführen." */
      teile.push('   Rechnung bzw. Positionen müssen wie folgt geändert werden:');
      for (const a of ab) teile.push(`   ${a}`);
    }
    /* .612: OHNE ANGEHAKTE ÄNDERUNG DER KURZE STANDARDTEXT (Marvin: „bitte Rechnung für Tiefbau ... beim Monteur
       halt bitte Rechnung für Montage einreichen"). */
    if (!ab.length && !nicht.length) teile.push(standardSatz(z.gewerk_gruppe));
  }
  teile.push('', 'Bitte stell deine Rechnung wie gewohnt an invoice@mds-innovation.com. Auf die Rechnung gehören Baustellenadresse, KLS-ID und SM-Nummer, damit wir sie dem Auftrag zuordnen können.');
  return teile.join('\n');
}

/* DAS URTEIL DER GUTSCHRIFT (.215), dieselben Wörter wie unter „Neue
   Gutschriften" auf der LEB-Abrechnung. */
export const GUTSCHRIFT_URTEIL: Record<string, { kurz: string; farbe: string }> = {
  zu_wenig: { kurz: 'Gutschrift zu wenig', farbe: 'z-ueberfaellig' },
  zu_viel: { kurz: 'Gutschrift über unserem Wert', farbe: 'z-unterbrochen' },
  teils_offen: { kurz: 'Gutschrift teilweise geprüft', farbe: 'z-laufend' },
  nicht_gelesen: { kurz: 'Gutschrift nicht gelesen', farbe: 'z-unterbrochen' },
  passt: { kurz: 'Gutschrift passt', farbe: 'z-erledigt' },
};

/** Wo eine Zeile steht (.365).
 *
 * ORIGINALTEXT MARVIN (14.10.2026)
 *   „was kam als Gutschrift zurück, was haben wir eingereicht bei der
 *    Telekom, was hat der Tiefbauer in seinem Aufmaß stehen, positiv oder
 *    negativ. Wenn es positiv für uns ist, kriegt er die Meldung, dass er
 *    sein Aufmaß so schreiben kann ... Ist es negativ zu unseren Lasten, dass
 *    er mehr eingestellt hat und wir was korrigiert haben? Dann kriegt er
 *    über das System die Meldung, hier beim Auftrag XY musst du die und die
 *    Position abändern."
 *
 *   passt       Gutschrift deckt das Aufmaß der Firma: Freigabe wie eingereicht.
 *   drunter     die Firma hat mehr aufgeschrieben, als gutgeschrieben wurde:
 *               Rechnung mit Änderungen (rot, Positionen anklickbar).
 *   drueber     die Telekom schreibt mehr gut, als die Firma aufgemessen hat:
 *               bleibt beim Aufmaß, oder Mehrmengen anfordern (rot, anklickbar).
 *   korrigiert  LEB und Gutschrift weichen voneinander ab, die Telekom hat in
 *               der E-Vergabe geändert; für die Firma ändert sich nichts
 *               (orange, Hinweis). Bei der Montage der einzige Abgleich.
 *   ohne        die Firma hat wohl nichts gemacht: erst entscheiden. */
type Lage = 'passt' | 'drueber' | 'drunter' | 'korrigiert' | 'ohne';
export function lageVon(z: any): Lage {
  if (z.ohne_leistung) return 'ohne';
  const v: any[] = z.vergleich ?? [];
  if (z.vergleicht_aufmass !== false) {
    if (v.some((p) => p.menge_aufmass > 0 && p.menge_gutschrift < p.menge_aufmass) || (z.abweichung_zeilen ?? []).length) return 'drunter';
    if (v.some((p) => !p.fremd && p.menge_gutschrift > p.menge_aufmass)) return 'drueber';
  }
  if (Number(z.telekom_korrigiert ?? 0) > 0) return 'korrigiert';
  if (z.urteil === 'zu_viel' || z.urteil === 'zu_wenig') return 'korrigiert';
  const w = z.werte;
  if (w && w.unser != null && w.gutschrift != null && Math.abs(w.gutschrift - w.unser) > 0.5) return 'korrigiert';
  return 'passt';
}
const LAGEWORT: Record<Lage, string> = { passt: 'passt', drueber: 'Gutschrift über dem Aufmaß', drunter: 'Rechnung anders als Aufmaß', korrigiert: 'Telekom hat korrigiert', ohne: 'keine Rechnung nötig?' };
const mengeText = (x: number, e: string | null) => `${String(Math.round(x * 1000) / 1000).replace('.', ',')}${e ? ' ' + e : ''}`;
/** Die Mehrmengen als Zeilen für die Nachricht (nur wenn gewählt). */
export function mehrZeilen(z: any): string[] {
  /* NICHT AUS DEM AUFMASS EINER ANDEREN FIRMA (.399). */
  return (z.vergleich ?? []).filter((p: any) => !p.fremd && p.menge_gutschrift > p.menge_aufmass)
    .map((p: any) => `${p.oz}${p.bezeichnung ? ' ' + p.bezeichnung : ''}${p.vermerk ? ` (${p.vermerk})` : ''}: ${mengeText(p.menge_gutschrift, p.einheit)} statt ${mengeText(p.menge_aufmass, p.einheit)}`);
}

/** Ein „?" mit Erklärung: beim Darüberfahren und beim Tippen (Telefon). */
export function Frage({ text, offen, schalte }: { text: string; offen: boolean; schalte: () => void }) {
  return (
    <span className={`frage ${offen ? 'offen' : ''}`}>
      <button type="button" className="frageknopf" aria-label="Hinweis" aria-expanded={offen}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); schalte(); }}>?</button>
      <span className="fragetext" role="tooltip">{text}</span>
    </span>
  );
}


/**
 * DAS BEKANNTE AUSKLAPPMENÜ (.388), aus der Zeile herausgelöst: die drei
 * Spalten, die drei Wege für die Nachricht, „Keine Rechnung nötig" und die
 * Gutschrift-PDFs. Steht einmal hier und wird von der Firmenliste UND der
 * Gutschriftliste gezeichnet - zwei Fassungen liefen beim ersten Nachtrag
 * auseinander.
 */
export function Positionsblock({ z, k, l, ohne, setOhne, mehr, setMehr, laeuft, ohneRechnung, darfGeld, hinweis, setHinweis, nicht, setNicht }: {
  z: any; k: string; l: Lage; ohne: Set<string>; setOhne: (s: Set<string>) => void; mehr: Set<string>; setMehr: (s: Set<string>) => void;
  /** NICHT DEINE POSITION (.410). ORIGINALTEXT MARVIN (19.10.2026): „Ich muss der
   *  Firma hier doch bei Rechnungsstellung jetzt mitteilen, dass sie Positionen
   *  nicht abrechnen sollen. Mir fehlen hier Checkboxen, dass alles angehakt ist
   *  bzw. aus ist. Und dass ich dann sagen kann, so, das ist aber nicht deine
   *  Position." Jede Zeile hat ein Häkchen, alles ist angehakt; was Marvin
   *  abhakt, steht im Push unter „Nicht auf die Rechnung". Schlüssel: `k|zeile`. */
  nicht?: Set<string>; setNicht?: (s: Set<string>) => void;
  laeuft: boolean; ohneRechnung: (z: any) => void; darfGeld: boolean;
  /** FREITEXT AN DIE FIRMA (.399). Marvin, 17.10.2026: „ein Freitextfeld, wo ich jetzt sagen kann, ihr halbe Position und halber Abzug". */
  hinweis?: string; setHinweis?: (t: string) => void;
}) {
  return (
    <>

                {(z.vergleich ?? []).length > 0 ? (
                  <div className={`ga-pos ${z.vergleicht_aufmass === false ? 'ohne-aufmass' : ''}`}>
                    {/* DREI SPALTEN (.365): Aufmaß der Firma, was wir
                        eingereicht haben, was die Telekom gutschreibt.
                        „Für die Rechnung" ist Gutschrift gegen Aufmaß:
                        das muss die Firma anders schreiben. Die Montage
                        hat kein Aufmaß in OZ, bei ihr stehen LEB und
                        Gutschrift nebeneinander. */}
                    <div className="ga-pos-kopf"><span>{setNicht ? <input type="checkbox" className="ga-haken" title="Alle Positionen anhaken (ihre Änderungen stehen im Push) oder keine (Standardtext)" disabled={laeuft}
                        checked={(z.vergleich as any[]).every((p: any, i: number) => p.fremd || !nicht?.has(`${k}|${posKennung(p, i)}`))}
                        onChange={(e) => { const n = new Set(nicht ?? []); (z.vergleich as any[]).forEach((p: any, i: number) => { const s = `${k}|${posKennung(p, i)}`; if (e.target.checked) n.delete(s); else if (!p.fremd) n.add(s); }); setNicht(n); }} /> : null} Position</span>{z.vergleicht_aufmass !== false && <span className="r">Aufmaß Firma</span>}<span className="r">LEB eingereicht</span><span className="r">Gutschrift</span><span className="r">{z.vergleicht_aufmass !== false ? 'Auf die Rechnung' : 'Telekom'}</span></div>
                    {(z.vergleich as any[]).map((p, i) => {
                      const mitAufmass = z.vergleicht_aufmass !== false;
                      const dm = mitAufmass ? p.menge_gutschrift - p.menge_aufmass : p.menge_gutschrift - p.menge_leb;
                      const tk = Math.abs(p.menge_gutschrift - p.menge_leb) > 0.0005;
                      /* AUFMASS EINER ANDEREN FIRMA (.399): steht dabei, gehört aber
                         nicht auf diese Rechnung und wird nicht als Mehrmenge angeboten. */
                      const fremd = !!p.fremd && mitAufmass;
                      const ks = `${k}|${posKennung(p, i)}`;
                      const weg = !fremd && !!nicht?.has(ks);
                      return (
                        <div key={p.zeile ?? `${p.oz}#${i}`} className={`ga-pos-zeile ${fremd ? 'fremd' : weg ? 'ohne-hinweis' : Math.abs(dm) < 0.0005 ? '' : dm > 0 ? 'gruen' : 'rot'}`}>
                          {/* JEDE LEB-ZEILE FÜR SICH, MIT VERMERK (.392): Grube 1, Graben 2. */}
                          <span>
                            {setNicht && !fremd && (
                              <input type="checkbox" className="ga-haken" checked={!weg} disabled={laeuft}
                                     title={weg ? 'Nicht angehakt: steht nicht im Push, die Firma rechnet nach ihrem Aufmaß ab' : 'Angehakt: die Änderung dieser Position steht im Push'}
                                     onChange={(e) => { const n = new Set(nicht ?? []); if (e.target.checked) n.delete(ks); else n.add(ks); setNicht(n); }} />
                            )}
                            {p.oz}{p.bezeichnung ? ` ${p.bezeichnung}` : ''}{p.vermerk ? <span className="ga-vermerk">, {p.vermerk}</span> : null}
                          </span>
                          {mitAufmass && <span className="r" data-titel="Aufmaß Firma">{fremd ? <span className="leise">andere Firma</span> : mengeText(p.menge_aufmass, p.einheit)}</span>}
                          <span className={`r ${tk ? (p.menge_gutschrift > p.menge_leb ? 'gruen' : 'rot') : ''}`} data-titel="LEB eingereicht">{mengeText(p.menge_leb, p.einheit)}</span>
                          <span className="r" data-titel="Gutschrift">{mengeText(p.menge_gutschrift, p.einheit)}</span>
                          <span className="r" data-titel={mitAufmass ? 'Auf die Rechnung' : 'Telekom'}>
                            {fremd ? <span className="leise">nicht auf diese Rechnung</span>
                              /* .612: NICHT ANGEHAKT HEISST: WIE IM AUFMASS, KEIN WORT IM PUSH. Marvin, 03.11.2026: „wenn ich eine
                                 Position habe ... wo nichts zu ändern ist, ich die so weitergebe, dann deaktiviere ich ja den Haken und
                                 damit geht das, was er ja auch dann im Aufmaß geschrieben hat, soll er damit in seine Rechnung
                                 übernehmen. Das ist halt irreführend, wenn da jetzt steht, entfällt." */
                              : weg ? <span className="leise">{mitAufmass ? `wie im Aufmaß (${mengeText(p.menge_aufmass, p.einheit)})` : 'wie eingereicht'}</span>
                              : Math.abs(dm) < 0.0005 ? '✓'
                              : mitAufmass
                                ? (dm < 0 ? `${mengeText(p.menge_gutschrift, p.einheit)} statt ${mengeText(p.menge_aufmass, p.einheit)}` : `+${mengeText(dm, p.einheit)} möglich`)
                                : `${dm > 0 ? '+' : '−'}${mengeText(Math.abs(dm), p.einheit)}`}
                          </span>
                        </div>
                      );
                    })}
                    {z.vergleicht_aufmass === false && <p className="klein leise" style={{ margin: '6px 0 0' }}>Montage: die Firma rechnet über ihre Standardpositionen ab, ihr Aufmaß wird hier nicht verglichen. Orange heißt: die Telekom hat gegenüber dem eingereichten LEB geändert.</p>}
                  </div>
                ) : <p className="klein leise" style={{ margin: 0 }}>Die Gutschrift hat keine gelesenen Positionen; verglichen werden nur die Beträge.</p>}
                {setHinweis && l !== 'ohne' && (
                  <label className="ga-hinweis">
                    <span className="klein leise">Hinweis an die Firma (kommt in den Push, z. B. „halbe Position, halber Abzug"; nie Preise)</span>
                    <input type="text" value={hinweis ?? ''} maxLength={500} disabled={laeuft}
                           placeholder="z. B. halbe Position, halber Abzug" onChange={(e) => setHinweis(e.target.value)} />
                  </label>
                )}
                <div className="reihe" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 8 }}>
                  {/* DREI WEGE FÜR DIE NACHRICHT (.377): mit den Positionen, die
                      die Plattform rechnet; dazu die Mehrmengen; oder wie
                      eingereicht ohne Positionen. Marvin entscheidet. */}
                  {z.vergleicht_aufmass !== false && ((z.abweichung_zeilen ?? []).length > 0 || mehrZeilen(z).length > 0) && (
                    <>
                      <button type="button" className={`knopf klein ${!ohne.has(k) && !mehr.has(k) ? 'haupt' : ''}`} aria-pressed={!ohne.has(k) && !mehr.has(k)}
                              title="Die Nachricht nennt die Positionen, die auf der Rechnung anders stehen müssen als im Aufmaß"
                              onClick={() => { const o = new Set(ohne); o.delete(k); setOhne(o); const m = new Set(mehr); m.delete(k); setMehr(m); }}>
                        Mit Positionen ({(z.abweichung_zeilen ?? []).length})
                      </button>
                      {mehrZeilen(z).length > 0 && (
                        <button type="button" className={`knopf klein ${mehr.has(k) && !ohne.has(k) ? 'haupt' : ''}`} aria-pressed={mehr.has(k) && !ohne.has(k)}
                                title="Dazu die Positionen, die die Telekom höher gutschreibt, als die Firma aufgemessen hat"
                                onClick={() => { const o = new Set(ohne); o.delete(k); setOhne(o); const m = new Set(mehr); m.add(k); setMehr(m); }}>
                          Mehrmengen dazu ({mehrZeilen(z).length})
                        </button>
                      )}
                      <button type="button" className={`knopf klein ${ohne.has(k) ? 'haupt' : ''}`} aria-pressed={ohne.has(k)}
                              title="Freigabe ohne Positionen: die Firma stellt die Rechnung wie eingereicht"
                              onClick={() => { const o = new Set(ohne); o.add(k); setOhne(o); }}>
                        Wie eingereicht, ohne Positionen
                      </button>
                    </>
                  )}
                  {l !== 'ohne' && (
                    <button type="button" className="knopf klein" disabled={laeuft}
                            title="Die Firma war gebucht, hat aber keine Leistung erbracht: ohne Nachricht erledigen"
                            onClick={() => ohneRechnung(z)}>Keine Rechnung nötig</button>
                  )}
                  {(z.gutschriften ?? []).map((g: any) => (
                    darfGeld && g.datei_da
                      ? <button key={g.id} type="button" className="alslink klein"
                                onClick={() => extern(api.gutschriftAdresse(g.id, `Gutschrift_${g.nummer ?? g.belegnummer ?? ''}.pdf`))}>
                          Gutschrift {g.nummer ?? g.belegnummer ?? ''}{g.datum ? ` vom ${datum(g.datum)}` : ''} (PDF)
                        </button>
                      : <span key={g.id} className="klein leise">Gutschrift {g.nummer ?? g.belegnummer ?? ''}{g.datum ? ` vom ${datum(g.datum)}` : ''}{g.datei_da ? '' : ' (ohne Datei)'}</span>
                  ))}
                </div>

    </>
  );
}

/* `oeffne` (.397): Marvin, 17.10.2026: „Ich möchte von der Maske auch direkt
   zum Auftrag springen können, also oben anklickbar machen bitte ... muss mir
   mein Bild angucken und so Sachen." Die Anschrift im Kopf jeder Gutschrift
   öffnet die Akte. */
export function Abrechnungsfreigabe({ ich, suche = '', oeffne }: { ich?: Anmelder; suche?: string; oeffne?: (id: string, reiter?: string) => void }) {
  const [pruefe, setPruefe] = useState<string | null>(null);
  /* MIT GEDÄCHTNIS (.398), siehe `merkplatz.ts`: beim Zurückkommen aus der
     Akte stehen die Daten, die Auswahl und die aufgeklappten Zeilen sofort
     wieder da; frisch geladen wird im Hintergrund. */
  const [fehler, setFehler] = useState<any>(null);
  const [d, setD, nachladen] = useMerkDaten<any>('abrechnung.bereit', () => api.freigabenBereit(), (f) => setFehler(ausFehler(f)));
  const [gewaehlt, setGewaehlt] = useMerkState<Set<string>>('abrechnung.gewaehlt', () => new Set());
  const [mehr, setMehr] = useState<Set<string>>(new Set());
  /* WIE EINGEREICHT (.377): die Nachricht geht ohne Positionen, auch wenn die
     Plattform Unterschiede rechnet. Marvin, 16.10.2026: „ich kann auch nie
     quittieren 'wie eingereicht' ... sondern nur mit änderungen." */
  const [ohne, setOhne] = useState<Set<string>>(new Set());
  /* NICHT DEINE POSITION (.410), überlebt den Sprung in die Akte. */
  const [nicht, setNicht] = useMerkState<Set<string>>('abrechnung.nicht', () => new Set());
  /* FREITEXT JE ZEILE (.399), überlebt den Sprung in die Akte. */
  const [hinweise, setHinweise] = useMerkState<Record<string, string>>('abrechnung.hinweise', () => ({}));
  const [auf, setAuf] = useMerkState<Set<string>>('abrechnung.auf', () => new Set());
  const [frage, setFrage] = useState<string | null>(null);
  const [firma, setFirma] = useMerkState<string | null>('abrechnung.firma', null);
  const [filter, setFilter] = useMerkState<'alle' | Lage>('abrechnung.filter', 'alle');
  const [hinweis, setHinweis] = useState<string | null>(null);
  useRollstelle('abrechnung.schritt1', !!d);
  const [laeuft, setLaeuft] = useState(false);
  const [zuletztOffen, setZuletztOffen] = useState(false);
  /* NACH GUTSCHRIFTEN ODER NACH FIRMEN (.388). Marvin, 17.10.2026: „dass wir
     uns an den Gutschriften orientieren und nicht die Firmen zuordnen."
     Die Gutschriftliste ist der Einstieg; die Firmenliste bleibt für den
     Sammel-Push an eine Firma. */
  const [ansicht, setAnsicht] = useState<'gutschrift' | 'firma'>(() => {
    try { return localStorage.getItem('abrechnung.schritt1') === 'firma' ? 'firma' : 'gutschrift'; } catch { return 'gutschrift'; }
  });
  const wechsle = (a: 'gutschrift' | 'firma') => { setAnsicht(a); try { localStorage.setItem('abrechnung.schritt1', a); } catch { /* egal */ } };

  async function laden(vorwahl = false) {
    const n = await nachladen();
    if (!n) return;
    if (vorwahl) setGewaehlt(new Set(n.zeilen.filter((z: any) => !z.altbestand && lageVon(z) === 'passt').map(schluessel)));
  }
  /* DIE VORWAHL NUR BEIM ERSTEN LADEN; wer zurückkommt, behält seine Auswahl. */
  useEffect(() => { if (!d) laden(true); }, []);
  useFrisch(() => laden(false));
  /* DER „?"-HINWEIS GEHT WIEDER ZU (.375). Marvin, 16.10.2026: „außerdem
     verschwindet der hinweis nicht mehr." Er ging nur über denselben Knopf zu;
     jetzt auch bei einem Klick irgendwo sonst und bei Escape. */
  useEffect(() => {
    if (!frage) return;
    const zu = (e: MouseEvent) => { if (!(e.target as HTMLElement)?.closest?.('.frage')) setFrage(null); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setFrage(null); };
    document.addEventListener('mousedown', zu); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', zu); document.removeEventListener('keydown', esc); };
  }, [frage]);

  const jeFirma = useMemo(() => {
    const m = new Map<string, any[]>();
    /* DIE SUCHE DER SEITE (.300): KLS-ID, SM, Anschrift, Firma. */
    /* OHNE ALTBESTAND (.390): die Firmenliste zeigt nur, was seit dem Stichtag kam. */
    for (const z of (d?.zeilen ?? []).filter((z: any) => !z.altbestand && passtSuche(suche, z.kls_id, z.sm_nummer, anschriftVon(z), z.firma, z.rechnung_nummer))) {
      m.set(z.firma, [...(m.get(z.firma) ?? []), z]);
    }
    /* ROT ZUERST: die Firma mit den meisten Zeilen über unserem Wert steht vorn. */
    return [...m.entries()].sort((a, b) =>
      b[1].filter((z) => lageVon(z) === 'drunter').length - a[1].filter((z) => lageVon(z) === 'drunter').length
      || a[0].localeCompare(b[0], 'de'));
  }, [d, suche]);
  const aktiv = jeFirma.find(([f]) => f === firma) ?? jeFirma[0];
  const zeilen = aktiv?.[1] ?? [];
  const sicht = zeilen.filter((z) => filter === 'alle' || lageVon(z) === filter);
  const gewaehltHier = zeilen.filter((z) => gewaehlt.has(schluessel(z)));

  const schalteIn = (set: Set<string>, setze: (s: Set<string>) => void, k: string) => {
    const n = new Set(set); if (n.has(k)) n.delete(k); else n.add(k); setze(n);
  };

  async function los(schonFreigegeben = false) {
    const eintraege = gewaehltHier.map((z: any) => ({ auftrag_id: z.auftrag_id, subunternehmer_id: z.subunternehmer_id, gutschrift_id: z.gutschrift_id ?? null,
                                                      gewerk_gruppe: z.gewerk_gruppe, mehr: mehr.has(schluessel(z)), wie_eingereicht: ohne.has(schluessel(z)),
                                                      hinweis: (hinweise[schluessel(z)] ?? '').trim() || null,
                                                      nicht: nichtZeilen(z, schluessel(z), nicht), nicht_pos: nichtPos(z, schluessel(z), nicht) }));
    if (!eintraege.length) return;
    if (!await fragen(schonFreigegeben
      ? `${eintraege.length} als erledigt festhalten? ${aktiv?.[0]} bekommt KEINE Nachricht.`
      : `${aktiv?.[0]}: ${eintraege.length} zur Abrechnung freigeben? Die Firma bekommt EINE Push-Nachricht `
        + 'in Glocke und Telegram, mit den Positionen, die auf der Rechnung anders stehen müssen als in ihrem Aufmaß. Nur Mengen, nie Preise.')) return;
    setLaeuft(true); setFehler(null); setHinweis(null);
    try {
      const e = await api.freigeben(eintraege, schonFreigegeben);
      setHinweis(e.klartext);
      setGewaehlt(new Set([...gewaehlt].filter((k) => !eintraege.some((x) => schluessel(x) === k))));
      await laden(false);
    } catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(false); }
  }

  /* „FREIGABE OHNE ABWEICHUNG VON SELBST SENDEN" (.340), je Firma. */
  async function vonSelbst(z: any, an: boolean) {
    try {
      await api.abrechnungVonSelbst(z.subunternehmer_id, an);
      setD((alt: any) => alt ? { ...alt, zeilen: alt.zeilen.map((x: any) => x.subunternehmer_id === z.subunternehmer_id ? { ...x, von_selbst: an } : x) } : alt);
    } catch (f) { setFehler(ausFehler(f)); }
  }

  /* „KEINE RECHNUNG NÖTIG" (.342). ORIGINALTEXT MARVIN (10.10.2026):
     „vielleicht war der Tiefbauer zwar drauf gebucht, hat es abgeschlossen,
     brauche aber keine Rechnung zu schreiben, weil er keine Leistungen
     erbracht hat. Das muss ich auch irgendwie selektieren." Die Zeile geht
     ohne Nachricht aus der Liste, die andere Firma bleibt stehen. */
  async function ohneRechnung(z: any) {
    const grund = await eingeben(`${z.firma}, ${GEWERK[z.gewerk_gruppe]} an ${voll(z)}: ohne Rechnung erledigen? `
      + 'Die Firma bekommt keine Nachricht. Grund (kommt in die Akte):', z.ohne_leistung ? 'keine Leistung erbracht' : '');
    if (grund === null) return;
    setLaeuft(true); setFehler(null); setHinweis(null);
    try {
      const e = await api.freigeben([{ auftrag_id: z.auftrag_id, subunternehmer_id: z.subunternehmer_id, gutschrift_id: z.gutschrift_id ?? null,
                                       gewerk_gruppe: z.gewerk_gruppe }], true, grund.trim() || 'keine Leistung erbracht');
      setHinweis(e.klartext);
      const n = new Set(gewaehlt); n.delete(schluessel(z)); setGewaehlt(n);
      await laden(false);
    } catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(false); }
  }

  async function zurueck(z: any) {
    const grund = await eingeben(`Freigabe für ${z.firma} (${z.strasse ?? ''} ${z.hausnummer ?? ''}) `
      + 'zurücknehmen? Grund (optional):', '');
    if (grund === null) return;
    try { await api.freigabeZuruecknehmen(z.id, grund || null); await laden(false); }
    catch (f) { setFehler(ausFehler(f)); }
  }

  /* BEIM ERSTEN ÖFFNEN STEHT DIE KARTE SOFORT DA, mit „lädt" statt mit
     nichts (.398): Marvin, 17.10.2026: „da braucht er mehrere Sekunden, um
     den oberen Teil zu laden ... Der untere Teil ist schon da". */
  if (!d) return (
    <Karte titel="Gutschrift da: Firma informieren">
      <Fehlerkasten lage={fehler} />
      {!fehler && <div className="klein leise" aria-busy="true">Gutschriften werden geladen …</div>}
    </Karte>
  );
  const darfGeld = !!ich && darf(ich, 'abrechnung.geld');
  /* WER SUCHT, SIEHT AUCH DIE FREIGEGEBENEN (.300): die Liste klappt auf. */
  const zuletztSicht = (d.zuletzt ?? []).filter((z: any) => passtSuche(suche, z.kls_id, z.sm_nummer, anschriftVon(z), z.firma));
  const zuletztSichtbar = zuletztOffen || !!suche.trim();
  const zaehl = (l: Lage) => zeilen.filter((z) => lageVon(z) === l).length;
  /* Die Vorschau zeigt die Mehrmengen mit, wenn sie gewählt sind. */
  const vorschau = gewaehltHier.map((z) => {
    const aus = nichtPos(z, schluessel(z), nicht);
    const nurAngehakt = (zl: string[]) => zl.filter((s) => !aus.some((a) => zeileGehoertZu(s, a)));
    return { ...(ohne.has(schluessel(z)) ? { ...z, abweichung_zeilen: [] }
      : mehr.has(schluessel(z)) ? { ...z, abweichung_zeilen: nurAngehakt([...(z.abweichung_zeilen ?? []), ...mehrZeilen(z)]) } : { ...z, abweichung_zeilen: nurAngehakt(z.abweichung_zeilen ?? []) }),
      hinweis: hinweise[schluessel(z)] ?? '', nicht_zeilen: [] };
  });

  return (
    <Karte titel={`Gutschrift da: Firma informieren (${d.zeilen.filter((z: any) => !z.altbestand).length})`}
           rechts={d.zeilen.length ? (
             <div className="ansichtwahl" role="tablist" aria-label="Ansicht">
               <button type="button" role="tab" aria-selected={ansicht === 'gutschrift'} className={`knopf klein ${ansicht === 'gutschrift' ? 'haupt' : ''}`} onClick={() => wechsle('gutschrift')}>Nach Gutschriften</button>
               <button type="button" role="tab" aria-selected={ansicht === 'firma'} className={`knopf klein ${ansicht === 'firma' ? 'haupt' : ''}`} onClick={() => wechsle('firma')}>Nach Firmen</button>
             </div>
           ) : undefined}>
      <Fehlerkasten lage={fehler} />
      {hinweis && <div className="hinweis">{hinweis}</div>}
      {d.zeilen.length > 0 && ansicht === 'gutschrift' ? (
        <Gutschriftliste ich={ich} d={d} suche={suche} laden={() => laden(false)} oeffne={oeffne}
                         entferne={(zs) => setD((alt: any) => alt ? { ...alt, zeilen: alt.zeilen.filter((x: any) => !zs.some((y) => schluessel(y) === schluessel(x))) } : alt)}
                         setHinweis={setHinweis} setFehler={setFehler} setPruefe={setPruefe} />
      ) : !d.zeilen.length ? (
        <p className="klein leise" style={{ margin: 0 }}>
          Nichts bereit: kein Auftrag, bei dem jedes LEB eine Gutschrift trägt
          und der noch nicht freigegeben ist.
        </p>
      ) : (
        <>
          {/* DIE FIRMEN OBEN (.358). */}
          <div className="firmenreiter" role="tablist" aria-label="Firma wählen">
            {jeFirma.map(([f, zz]) => {
              const rot = zz.filter((z) => lageVon(z) === 'drunter').length;
              return (
                <button key={f} type="button" role="tab" aria-selected={aktiv?.[0] === f}
                        className={`firmenreiter-knopf ${aktiv?.[0] === f ? 'an' : ''}`}
                        onClick={() => { setFirma(f); setFilter('alle'); }}>
                  {f}<span className="fr-zahl">{zz.length}</span>{rot > 0 && <span className="fr-rot" title={`${rot} Rechnung anders als Aufmaß`}>{rot}</span>}
                </button>
              );
            })}
          </div>

          {aktiv && (
            <div className="ga-platz">
              <div className="ga-leiste">
                <div className="ga-filter">
                  {(['alle', 'passt', 'drunter', 'drueber', 'korrigiert', 'ohne'] as const).map((f) => {
                    const n = f === 'alle' ? zeilen.length : zaehl(f);
                    if (f !== 'alle' && !n) return null;
                    return (
                      <button key={f} type="button" aria-pressed={filter === f}
                              className={`ga-chip ${f === 'drunter' ? 'rot' : f === 'drueber' ? 'gruen' : f === 'korrigiert' || f === 'ohne' ? 'orange' : ''} ${filter === f ? 'an' : ''}`}
                              onClick={() => setFilter(f)}>
                        {f === 'alle' ? 'alle' : LAGEWORT[f]} {n}
                      </button>
                    );
                  })}
                </div>
                <div className="ga-tun">
                  {ich && (
                    <label className="klein leise" style={{ cursor: 'pointer' }}
                           title="Stimmt die Gutschrift mit dem eingereichten Aufmaß überein, geht die Push-Nachricht ohne Klick hinaus (stündlich).">
                      <input type="checkbox" checked={!!zeilen[0]?.von_selbst}
                             onChange={(e) => vonSelbst(zeilen[0], e.target.checked)} />{' '}
                      ohne Abweichung von selbst senden
                    </label>
                  )}
                  {/* OHNE GRUPPE KEIN TELEGRAM (23.09.2026). */}
                  {!zeilen[0]?.gruppe_da && (
                    <span className="marke-zustand z-neu" title="Stammdaten, Reiter „Firmen“, Feld „Telegram-Gruppe Freigaben“">keine Telegram-Gruppe</span>
                  )}
                  <button className="knopf klein" disabled={!gewaehltHier.length || laeuft}
                          title="Festhalten ohne Nachricht an die Firma (sie hat die Rechnung schon gestellt)"
                          onClick={() => los(true)}>Erledigt, ohne Nachricht ({gewaehltHier.length})</button>
                  <button className="knopf haupt" disabled={!gewaehltHier.length || laeuft} onClick={() => los(false)}>
                    {laeuft ? 'läuft …' : `${aktiv[0]} informieren (${gewaehltHier.length})`}
                  </button>
                </div>
              </div>

              <div className="ga-kopf" aria-hidden="true">
                <span>
                  <input type="checkbox" aria-label="alle wählen"
                         checked={sicht.length > 0 && sicht.every((z) => gewaehlt.has(schluessel(z)))}
                         onChange={() => {
                           const alle = sicht.every((z) => gewaehlt.has(schluessel(z)));
                           const n = new Set(gewaehlt);
                           for (const z of sicht) { if (alle) n.delete(schluessel(z)); else n.add(schluessel(z)); }
                           setGewaehlt(n);
                         }} />
                </span>
                <span>Auftrag</span><span className="r">Unser Wert</span><span className="r">Gutschrift</span><span>Abgleich</span><span>Nachricht an die Firma</span>
              </div>
              {sicht.map((z) => {
                const k = schluessel(z);
                const l = lageVon(z);
                const w = z.werte;
                const diff = w && w.unser != null && w.gutschrift != null ? w.gutschrift - w.unser : null;
                const ab = ohne.has(k) ? 0 : [...(z.abweichung_zeilen ?? []), ...(mehr.has(k) ? mehrZeilen(z) : [])].filter((s: string) => !nichtPos(z, k, nicht).some((a) => zeileGehoertZu(s, a))).length;
                const offen = auf.has(k);
                const u = z.urteil ? GUTSCHRIFT_URTEIL[z.urteil] : null;
                return (
                  <div key={k} className={`ga-zeile lage-${l} ${offen ? 'offen' : ''}`}>
                    <div className="ga-haupt">
                      <span className="ga-wahl">
                        <input type="checkbox" aria-label="auswählen" checked={gewaehlt.has(k)} onChange={() => schalteIn(gewaehlt, setGewaehlt, k)} />
                      </span>
                      <span className="ga-auftrag">
                        {/* DIE VOLLE ANSCHRIFT MIT PLZ (23.09.2026). */}
                        <b>
                          {oeffne && z.auftrag_id
                            ? <button type="button" className="alslink gl-zum-auftrag" title="Akte öffnen: Bilder, Aufmaß, Verlauf" onClick={() => oeffne(z.auftrag_id, 'uebersicht')}>{voll(z) || 'ohne Anschrift'}</button>
                            : (voll(z) || 'ohne Anschrift')}
                          {z.maengelbeseitigung && <span className="marke-zustand z-neu gl-mb">Mängelbeseitigung</span>}
                        </b>
                        <span className="klein leise">{GEWERK[z.gewerk_gruppe]}{z.aus_aufmass ? ', aus dem Aufmaß, nicht gebucht' : ''}{z.gutschrift_nummer ? `, Gutschrift ${z.gutschrift_nummer}` : ''}{z.gutschrift_eingang ? ` vom ${datum(z.gutschrift_eingang)}` : z.gutschrift_zuletzt ? `, Gutschrift ${datum(z.gutschrift_zuletzt)}` : ''}</span>
                        <NummernKopieren kls={z.kls_id} sm={z.sm_nummer} />
                      </span>
                      <span className="ga-wert r" data-titel="LEB eingereicht">{w?.unser != null ? euro(w.unser) : <span className="leise">{darfGeld ? '–' : ''}</span>}</span>
                      {/* ROT UND GRÜN NACH VORTEIL (.377). Marvin, 16.10.2026: „wenn der
                          vorteil bei uns liegt muss es grün sein, ansonsten rot."
                          Gutschrift über unserem Wert: grün. Darunter: rot. */}
                      <span className={`ga-wert r ${diff != null && Math.abs(diff) > 0.5 ? (diff > 0 ? 'gruen' : 'rot') : ''}`} data-titel="Gutschrift">
                        {w?.gutschrift != null ? euro(w.gutschrift) : <span className="leise">{darfGeld ? '–' : ''}</span>}
                      </span>
                      <span className="ga-abgleich">
                        {l === 'passt' ? <span className="ga-passt">✓ passt</span>
                          : l === 'ohne' ? (
                            <span className="ga-ohne">keine Leistung?
                              <Frage text={z.ohne_leistung} offen={frage === k + 'o'} schalte={() => setFrage(frage === k + 'o' ? null : k + 'o')} />
                            </span>)
                          : (
                            <button type="button" className={`ga-abw ${l === 'drueber' ? 'gruen' : l === 'drunter' ? 'rot' : (diff ?? 0) > 0.5 ? 'gruen' : (diff ?? 0) < -0.5 ? 'rot' : 'orange'}`} aria-expanded={offen}
                                    onClick={() => schalteIn(auf, setAuf, k)}
                                    title={l === 'korrigiert' ? 'Die Gutschrift weicht vom eingereichten LEB ab: die Telekom hat in der E-Vergabe geändert. Für die Rechnung der Firma ändert das nichts.' : 'Positionen aufklappen'}>
                              {l === 'korrigiert' && diff != null && Math.abs(diff) > 0.5
                                ? `Telekom ${diff > 0 ? '+' : '−'}${euro(Math.abs(diff))}`
                                : LAGEWORT[l]}
                              <span aria-hidden="true">{offen ? ' ▴' : ' ▾'}</span>
                            </button>)}
                        {u && z.urteil !== 'passt' && z.urteil_text && (
                          <Frage text={`${u.kurz}: ${z.urteil_text}`} offen={frage === k + 'u'} schalte={() => setFrage(frage === k + 'u' ? null : k + 'u')} />
                        )}
                        {/* BITTE PRÜFEN (.365): Fotos, Pipelänge, Fertigmeldung. */}
                        {!!(z.pruefhinweise ?? []).length && (
                          <span className="ga-pruefen">bitte prüfen
                            <Frage text={(z.pruefhinweise as string[]).join(' ')} offen={frage === k + 'p'} schalte={() => setFrage(frage === k + 'p' ? null : k + 'p')} />
                          </span>
                        )}
                      </span>
                      <span className="ga-nachricht">
                        {l === 'ohne' ? 'erst entscheiden'
                          : ab ? `Rechnung mit ${ab} ${ab === 1 ? 'angepasster Position' : 'angepassten Positionen'}`
                          : 'Freigabe, bitte Rechnung'}
                        {z.rechnung
                          ? <RechnungChip r={z.rechnung} pruefen={ich ? setPruefe : undefined} />
                          : z.rechnung_da ? <span className="marke-zustand z-laufend">Rechnung liegt vor{z.rechnung_nummer ? ` (${z.rechnung_nummer})` : ''}</span> : null}
                        {l !== 'ohne' && !offen && (z.vergleich ?? []).length > 0 && (
                          <button type="button" className="alslink klein" onClick={() => schalteIn(auf, setAuf, k)}>Positionen</button>
                        )}
                        {l === 'ohne' && (
                          <button type="button" className="knopf klein" disabled={laeuft} onClick={() => ohneRechnung(z)}>Keine Rechnung nötig</button>
                        )}
                      </span>
                    </div>
                    {(offen || l === 'ohne') && (
                      <div className="ga-auf">
                        <Positionsblock z={z} k={k} l={l} ohne={ohne} setOhne={setOhne} mehr={mehr} setMehr={setMehr} nicht={nicht} setNicht={setNicht}
                                        laeuft={laeuft} ohneRechnung={ohneRechnung} darfGeld={darfGeld}
                                        hinweis={hinweise[k] ?? ''} setHinweis={(t) => setHinweise({ ...hinweise, [k]: t })} />
                      </div>
                    )}
                  </div>
                );
              })}
              {!sicht.length && <p className="klein leise" style={{ margin: '10px 0' }}>Nichts in diesem Filter.</p>}

              {gewaehltHier.length > 0 && (
                <div className="pushvorschau" style={{ marginTop: 12 }}>
                  <div className="klein leise" style={{ marginBottom: 4 }}>Die Nachricht an {aktiv[0]}, so wie sie rausgeht (nur Mengen, nie Preise):</div>
                  <pre className="pushtext">{pushVorschau(vorschau.slice(0, 15))}</pre>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {(d.zuletzt ?? []).length > 0 && (!suche.trim() || zuletztSicht.length > 0) && (
        <div style={{ marginTop: 12 }}>
          <button className="knopf klein" onClick={() => setZuletztOffen(!zuletztOffen)}>
            {zuletztSichtbar ? 'Zuletzt freigegeben ausblenden'
              : `Zuletzt freigegeben (${zuletztSicht.length})`}
          </button>
          {zuletztSichtbar && (
            <div className="freigabeliste" style={{ marginTop: 8 }}>
              {zuletztSicht.map((z: any) => (
                <div key={z.id} className="freigabezeile">
                  <span className="freigabezeile-text">
                    <b>{z.firma}</b>, {voll(z)}
                    <span className="klein leise">
                      {' '}{GEWERK[z.gewerk_gruppe]}, {datum(z.freigegeben_am)}
                      {z.wie === 'automatisch' ? ', automatisch'
                        : z.wie === 'bestand' ? ', schon vorher, ohne Nachricht' : ''}
                    </span>
                  </span>
                  {z.rechnung_da
                    ? <span className="marke-zustand z-erledigt">Rechnung da</span>
                    : <span className="marke-zustand z-laufend">wartet auf Rechnung</span>}
                  {!z.rechnung_da && (
                    <button className="knopf klein" onClick={() => zurueck(z)}>zurücknehmen</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {pruefe && ich && (
        <Prueffenster ich={ich} id={pruefe} zu={() => setPruefe(null)}
                      fertig={() => laden(false).catch(() => {})} />
      )}
    </Karte>
  );
}
